import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash';
import {
  faBinoculars,
  faColumns,
  faHome,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@ebsco-ui/ebsco-ui';

import { Icon } from '@app/components';
import { useFeatureFlag, useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { FEATURE, ROUTE, SetState } from '@app/constants';

import { NavigationDropdown } from './NavigationDropdown';

import css from './NavigationPanel.module.scss';

interface NavigationPanelProps {
  isMobile?: boolean;
  setShowDrawer?: SetState<boolean>;
}

export const NavigationPanel: FC<NavigationPanelProps> = ({
  isMobile,
  setShowDrawer,
}) => {
  const { $t } = useIntl();
  const { isActive: isHoldsAndCheckoutsFlagActive } = useFeatureFlag({
    name: FEATURE.holdsAndCheckouts,
    isActiveIfNotFound: true,
  });
  const {
    holdsAndCheckoutsRoute,
    selectConfiguredOption: selectConfiguredHoldsAndCheckoutsOption,
  } = useHoldsAndCheckoutsPageConfig();

  const handleCloseMobileMenu = () => setShowDrawer && setShowDrawer(false);

  const browseDropdownLinks = useMemo(
    () => [
      {
        flags: [{ name: FEATURE.courseReserves }],
        label: $t({
          defaultMessage: 'Course reserves',
          id: 'courseReserves.title',
        }),
        href: ROUTE.courseReserves,
      },
      {
        flags: [{ name: FEATURE.callNumbers }],
        label: $t({
          defaultMessage: 'Call numbers',
          id: 'navigation.callNumbers',
        }),
        href: ROUTE.callNumbers,
      },
    ],
    [$t]
  );

  const dashboardDropdownLinks = useMemo(
    () =>
      compact([
        {
          label: $t(sharedMessages.projects),
          href: ROUTE.projects,
        },
        {
          label: $t(sharedMessages.saved),
          href: ROUTE.saved,
        },
        {
          label: $t(sharedMessages.recentActivity),
          href: ROUTE.recentActivity,
        },
        isHoldsAndCheckoutsFlagActive && {
          label: selectConfiguredHoldsAndCheckoutsOption(
            $t(sharedMessages.requestsAndLoans),
            $t(sharedMessages.holdsAndCheckouts)
          ),
          href: holdsAndCheckoutsRoute,
        },
      ]),
    [
      $t,
      holdsAndCheckoutsRoute,
      isHoldsAndCheckoutsFlagActive,
      selectConfiguredHoldsAndCheckoutsOption,
    ]
  );

  return (
    <nav
      className={css.navigation}
      aria-label={$t({
        id: 'navigation.main',
        defaultMessage: 'Main',
      })}
    >
      {isMobile && (
        <div className={css.closeButtonWrapper}>
          <Button
            styleType="flat-icon"
            className={css.closeButton}
            aria-label={$t(sharedMessages.close)}
            onClick={handleCloseMobileMenu}
          >
            <FontAwesomeIcon className="fa-lg" icon={faTimes} />
          </Button>
        </div>
      )}
      <ul className={css.links}>
        <li>
          <NavLink
            to={ROUTE.search}
            className={({ isActive }) =>
              cn(css.link, {
                [css.isActive]: isActive,
              })
            }
            onClick={handleCloseMobileMenu}
          >
            <Icon icon={faHome}>
              {$t({
                defaultMessage: 'Catalog home',
                id: 'navigation.catalogHome',
              })}
            </Icon>
          </NavLink>
        </li>
        <NavigationDropdown
          name="dashboard"
          icon={faColumns}
          links={dashboardDropdownLinks}
          isMobile={isMobile}
          setShowDrawer={setShowDrawer}
        />
        <NavigationDropdown
          name="browse"
          icon={faBinoculars}
          links={browseDropdownLinks}
          flags={[
            { name: FEATURE.callNumbers },
            { name: FEATURE.courseReserves },
          ]}
          isMobile={isMobile}
          setShowDrawer={setShowDrawer}
        />
      </ul>
    </nav>
  );
};
